import React, { useState, useEffect } from 'react';
import { Clock, ChevronLeft, ChevronRight, User, UserPlus, CreditCard, AlertTriangle, Mail, Phone, Calendar, MoreHorizontal, Stethoscope, MapPin, X } from 'lucide-react';
import { useParams } from 'react-router-dom';


const API_URL = process.env.REACT_APP_API_URL || 'https://centralsaludbackend-production.up.railway.app';


const ESTADOS_CITA = {
  programada: { label: 'Programada', color: '#3498db' },
  confirmada: { label: 'Confirmada', color: '#2ecc71' },
  en_curso: { label: 'En Curso', color: '#f39c12' },
  completada: { label: 'Completada', color: '#27ae60' },
  cancelada: { label: 'Cancelada', color: '#e74c3c' },
  no_asistio: { label: 'No Asistió', color: '#95a5a6' }
};

const ESTADOS_REGISTRO = {
  aprobada: { label: 'Aprobada', icon: '✓' },
  pendiente_aprobacion: { label: 'Pendiente de Aprobación', icon: '⏳' },
  rechazada: { label: 'Rechazada', icon: '✗' }
};

export default function CalendarioPublico() {
  const [clienteId, setClienteId] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [autoRegistroEnabled, setAutoRegistroEnabled] = useState(false);
  
  // Flujo de autenticación
  const [paso, setPaso] = useState('identificacion');
  const [tipoUsuario, setTipoUsuario] = useState(null);
  const [dni, setDni] = useState('');
  const [pacienteInfo, setPacienteInfo] = useState(null);
  
  // Datos del calendario
  const [citas, setCitas] = useState([]);
  const [mesActual, setMesActual] = useState(new Date());
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modales
  const [showCitaModal, setShowCitaModal] = useState(false);
  const [showConflictoModal, setShowConflictoModal] = useState(false);
  const [showTodasCitasModal, setShowTodasCitasModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [citasDelDiaSeleccionado, setCitasDelDiaSeleccionado] = useState([]);
  const [fechaDiaSeleccionado, setFechaDiaSeleccionado] = useState(null);
  const [conflictoData, setConflictoData] = useState(null);
  
  // Formularios
  const [citaForm, setCitaForm] = useState({
    dni: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    email: '',
    fecha: '',
    hora_inicio: '',
    hora_fin: '',
    motivo: '',
    notas: ''
  });

  const { token } = useParams();

  // Detectar parámetros de la URL
  useEffect(() => {
    if (!token) {
      setError('No se especificó el token de calendario');
      setLoading(false);
    } else {
      cargarInformacionClinica();
    }
  }, [token]);

  // Cargar información de la clínica
  useEffect(() => {
    if (clienteId) {
      cargarInformacionClinica();
    }
  }, [clienteId]);

  // Cargar citas cuando cambie el mes (solo si ya estamos en el calendario)
  useEffect(() => {
    if (paso === 'calendario' && pacienteInfo) {
      cargarCitasPaciente();
    }
  }, [paso, pacienteInfo, mesActual]);

  const cargarInformacionClinica = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // ⭐ CAMBIO: Usar ruta con token
      const response = await fetch(`${API_URL}/api/citas/publico/clinica/token/${token}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Token de calendario no válido o expirado');
        }
        throw new Error('No se pudo cargar la información de la clínica');
      }
      
      const data = await response.json();
      setClienteInfo(data.cliente);
      setAutoRegistroEnabled(data.cliente.auto_registro_enabled === 1 || data.cliente.auto_registro_enabled === true);
      
    } catch (err) {
      console.error('Error al cargar información de la clínica:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarCitasPaciente = async () => {
    try {
      // ⭐ CAMBIO: Usar ruta con token
      const response = await fetch(`${API_URL}/api/citas/publico/citas/paciente/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dni: pacienteInfo.dni, token: token }) // Incluir token
      });

      if (response.ok) {
        const data = await response.json();
        setCitas(data.citas || []);
      }
    } catch (err) {
      console.error('Error al cargar citas del paciente:', err);
    }
  };

  const handleIdentificacion = async (dniIngresado) => {
    setLoading(true);
    setError(null);
    
    try {
      // Validar formato de DNI
      const dniRegex = /^[0-9]{8}[A-Z]$/;
      if (!dniRegex.test(dniIngresado.toUpperCase())) {
        throw new Error('Por favor, ingrese un DNI válido (8 dígitos + letra, ej: 12345678A)');
      }

      setDni(dniIngresado.toUpperCase());

      // ⭐ CAMBIO: Usar ruta con token
      const response = await fetch(`${API_URL}/api/citas/publico/citas/paciente/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dni: dniIngresado.toUpperCase(), token: token }) // Incluir token
      });

      if (response.ok) {
        // Paciente existe - ir al calendario
        const data = await response.json();
        setPacienteInfo({
          id: data.paciente.id,
          dni: dniIngresado.toUpperCase(),
          nombre: data.paciente.nombre,
          apellidos: data.paciente.apellidos,
          telefono: data.paciente.telefono,
          email: data.paciente.email
        });
        setCitas(data.citas || []);
        setPaso('calendario');
      } else {
        // Paciente NO existe
        if (!autoRegistroEnabled) {
          throw new Error('No puede solicitar citas en esta clínica. Por favor, contacte directamente con la clínica para registrarse.');
        }
        
        // Auto-registro activado - mostrar formulario de nueva cita
        setPacienteInfo(null);
        setCitaForm({
          dni: dniIngresado.toUpperCase(),
          nombre: '',
          apellidos: '',
          telefono: '',
          email: '',
          fecha: new Date().toISOString().split('T')[0],
          hora_inicio: '09:00',
          hora_fin: '10:00',
          motivo: '',
          notas: ''
        });
        setPaso('calendario');
        setShowCitaModal(true);
      }
    } catch (err) {
      console.error('Error en identificación:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirModalNuevaCita = (fecha = null) => {
    const fechaParaForm = fecha || new Date();
    const year = fechaParaForm.getFullYear();
    const month = String(fechaParaForm.getMonth() + 1).padStart(2, '0');
    const day = String(fechaParaForm.getDate()).padStart(2, '0');
    const fechaStr = `${year}-${month}-${day}`;
    
    setCitaForm({
      dni: pacienteInfo.dni,
      nombre: pacienteInfo.nombre,
      apellidos: pacienteInfo.apellidos,
      telefono: pacienteInfo.telefono,
      email: pacienteInfo.email,
      fecha: fechaStr,
      hora_inicio: '09:00',
      hora_fin: '10:00',
      motivo: '',
      notas: ''
    });

    setShowCitaModal(true);
  };

  const guardarCita = async (e) => {
  e.preventDefault();
  
  console.log('📤 Datos del formulario antes de enviar:', citaForm);
  
  // Validaciones
  if (!citaForm.fecha || !citaForm.hora_inicio || !citaForm.hora_fin) {
    alert('Por favor, complete todos los campos obligatorios de la cita');
    return;
  }

  if (!citaForm.dni) {
    alert('El DNI es requerido');
    return;
  }

  // Validar datos del paciente
  if (!citaForm.nombre || !citaForm.apellidos || !citaForm.telefono || !citaForm.email) {
    alert('Por favor, complete todos los datos personales');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(citaForm.email)) {
    alert('Por favor, ingrese un email válido');
    return;
  }

  const telefonoLimpio = citaForm.telefono.replace(/\D/g, '');
  if (telefonoLimpio.length < 9) {
    alert('Por favor, ingrese un teléfono válido (mínimo 9 dígitos)');
    return;
  }

  try {
    const datosCita = {
      token: token, 
      dni: citaForm.dni,
      nombre: citaForm.nombre,
      apellidos: citaForm.apellidos,
      telefono: citaForm.telefono,
      email: citaForm.email,
      fecha: citaForm.fecha,
      hora_inicio: citaForm.hora_inicio,
      hora_fin: citaForm.hora_fin,
      motivo: citaForm.motivo || '',
      notas: citaForm.notas || ''
    };

    console.log('📤 Enviando datos al backend:', datosCita);

    // ⭐ CAMBIO: Usar ruta con token
    const response = await fetch(`${API_URL}/api/citas/publico/citas/crear/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(datosCita)
    });

    const data = await response.json();
    console.log('📥 Respuesta del backend:', data);

    if (!response.ok) {
      if (response.status === 409) {
        // Conflicto de horario
        setConflictoData({
          citaOriginal: citaForm,
          horariosAlternativos: data.horariosAlternativos || []
        });
        setShowConflictoModal(true);
        return;
      }
      
      if (response.status === 403) {
        // Auto-registro desactivado o paciente pendiente
        alert(data.message);
        return;
      }
      
      throw new Error(data.message || 'Error al crear la cita');
    }

    setShowCitaModal(false);

    if (data.estado_registro === 'pendiente_aprobacion') {
      // Mensaje diferenciado según tipo de paciente
      const mensaje = data.paciente_ya_registrado
        ? '✓ Su solicitud de cita ha sido enviada correctamente.\n\nLa cita está pendiente de aprobación por parte de la clínica.\nRecibirá una notificación por email cuando sea aprobada.'
        : '✓ Su solicitud de cita ha sido enviada correctamente.\n\nComo es su primera cita, tanto su registro como la cita están pendientes de aprobación.\nRecibirá una notificación por email cuando la clínica apruebe su solicitud.';
      
      alert(mensaje);
      
      // Si era paciente nuevo, mostrar confirmación
      if (!pacienteInfo) {
        setPaso('confirmacion_nuevo');
      } else {
        // Si es paciente registrado, recargar sus citas
        await cargarCitasPaciente();
      }
    } else {
      // Cita aprobada automáticamente
      alert('✓ Cita creada y confirmada correctamente.\n\nLa cita ha sido añadida al calendario de la clínica.');
      
      // Recargar citas si es paciente registrado
      if (pacienteInfo) {
        await cargarCitasPaciente();
      }
    }

  } catch (error) {
    console.error('❌ Error al guardar cita:', error);
    alert(`Error al guardar cita: ${error.message}`);
  }
};

  const guardarCitaConHorarioAlternativo = async (horario) => {
    const citaModificada = {
      ...conflictoData.citaOriginal,
      hora_inicio: horario.hora_inicio,
      hora_fin: horario.hora_fin
    };

    setCitaForm(citaModificada);
    setShowConflictoModal(false);

    setTimeout(() => {
      document.getElementById('form-nueva-cita')?.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      );
    }, 100);
  };

  // Funciones auxiliares del calendario
  const cambiarMes = (delta) => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + delta, 1));
  };

  const irHoy = () => {
    setMesActual(new Date());
  };

  const obtenerDiasDelMes = () => {
    const year = mesActual.getFullYear();
    const month = mesActual.getMonth();
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    const dias = [];
    const primerDiaSemana = primerDia.getDay();
    
    for (let i = primerDiaSemana; i > 0; i--) {
      dias.push({ fecha: new Date(year, month, 1 - i), mesActual: false });
    }
    
    for (let i = 1; i <= ultimoDia.getDate(); i++) {
      dias.push({ fecha: new Date(year, month, i), mesActual: true });
    }
    
    const diasRestantes = 7 - (dias.length % 7);
    if (diasRestantes < 7) {
      for (let i = 1; i <= diasRestantes; i++) {
        dias.push({ fecha: new Date(year, month + 1, i), mesActual: false });
      }
    }
    
    return dias;
  };

  const obtenerCitasDelDia = (fecha) => {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    const fechaStr = `${year}-${month}-${day}`;
    
    return citas.filter(c => {
      const citaFecha = c.fecha.includes('T') ? c.fecha.split('T')[0] : c.fecha;
      return citaFecha === fechaStr;
    }).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
  };

  const esHoy = (fecha) => {
    const hoy = new Date();
    return fecha.toDateString() === hoy.toDateString();
  };

  const formatearFechaLocal = (fechaStr) => {
    const [year, month, day] = fechaStr.split('T')[0].split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getColorCita = (cita) => {
    if (cita.medico_color) return cita.medico_color;
    if (cita.consulta_color) return cita.consulta_color;
    return ESTADOS_CITA[cita.estado]?.color || '#3498db';
  };

  const lightenColor = (color, percent = 85) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1);
  };

  const abrirModalTodasCitas = (fecha, citas) => {
    setFechaDiaSeleccionado(fecha);
    setCitasDelDiaSeleccionado(citas);
    setShowTodasCitasModal(true);
  };

  // Estados de carga
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f9fafb'
      }}>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={{ textAlign: 'center' }}>
          <div 
            style={{ 
              width: '40px', 
              height: '40px', 
              border: '4px solid #e5e7eb',
              borderTopColor: '#3cbf9a',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 12px'
            }}
          />
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#f9fafb',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center',
          maxWidth: '500px',
          padding: '32px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#1f2937', marginBottom: '8px', fontSize: '18px' }}>Error</h3>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>{error}</p>
        </div>
      </div>
    );
  }

  // Pantalla de confirmación para nuevos pacientes
  if (paso === 'confirmacion_nuevo') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            ✓
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
            Solicitud Enviada
          </h2>
          <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
            Su solicitud de cita ha sido enviada correctamente.
          </p>
          <div style={{
            background: '#e0f2fe',
            border: '1px solid #0ea5e9',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <p style={{ fontSize: '14px', color: '#075985', margin: 0, lineHeight: '1.6' }}>
              <strong>📧 Próximos pasos:</strong><br/>
              La clínica revisará su solicitud y le contactará pronto a través del email o teléfono que proporcionó.
            </p>
          </div>
          <p style={{ fontSize: '13px', color: '#9ca3af' }}>
            Puede cerrar esta ventana
          </p>
        </div>
      </div>
    );
  }

  // Pantalla de identificación
  if (paso === 'identificacion') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          maxWidth: autoRegistroEnabled ? '600px' : '450px',
          width: '100%',
          padding: '40px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Calendar size={40} color="white" />
            </div>
            <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
              {clienteInfo?.razon_social || 'Sistema de Citas'}
            </h2>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Identifíquese para continuar
            </p>
          </div>

          {!autoRegistroEnabled ? (
            // Solo mostrar input de DNI
            <form onSubmit={(e) => {
              e.preventDefault();
              const formDni = e.target.elements.dni.value;
              handleIdentificacion(formDni);
            }} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  <CreditCard size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  DNI *
                </label>
                <input
                  type="text"
                  name="dni"
                  placeholder="12345678A"
                  required
                  maxLength={9}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    textTransform: 'uppercase'
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#3cbf9a'}
                  onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                  Formato: 8 dígitos + letra (ej: 12345678A)
                </p>
              </div>

              <button
                type="submit"
                style={{
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              >
                Acceder
              </button>
            </form>
          ) : (
            // Mostrar opciones de tipo de usuario + input DNI
            <>
              {!tipoUsuario ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <button
                    onClick={() => setTipoUsuario('nuevo')}
                    style={{
                      padding: '30px 20px',
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3cbf9a';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 10px 20px rgba(60, 191, 154, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UserPlus size={30} color="white" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', margin: 0 }}>
                        Soy Nuevo Paciente
                      </h3>
                      <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5', margin: '8px 0 0 0' }}>
                        Primera vez en esta clínica
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={() => setTipoUsuario('registrado')}
                    style={{
                      padding: '30px 20px',
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#3cbf9a';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 10px 20px rgba(60, 191, 154, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: 'linear-gradient(135deg, #26a67a 0%, #1e8a66 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <User size={30} color="white" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px', margin: 0 }}>
                        Ya Estoy Registrado
                      </h3>
                      <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5', margin: '8px 0 0 0' }}>
                        Ya tengo citas previas
                      </p>
                    </div>
                  </button>
                </div>
              ) : (
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formDni = e.target.elements.dni.value;
                  handleIdentificacion(formDni);
                }} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {tipoUsuario === 'nuevo' && (
                    <div style={{
                      background: '#e0f2fe',
                      border: '1px solid #0ea5e9',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      color: '#075985'
                    }}>
                      <strong>ℹ️ Primera vez en nuestra clínica</strong>
                      <p style={{ margin: '8px 0 0 0' }}>
                        Su solicitud de cita será revisada y necesitará completar sus datos de contacto.
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      <CreditCard size={16} style={{ display: 'inline', marginRight: '6px' }} />
                      DNI *
                    </label>
                    <input
                      type="text"
                      name="dni"
                      placeholder="12345678A"
                      required
                      maxLength={9}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        textTransform: 'uppercase'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#3cbf9a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                      Formato: 8 dígitos + letra (ej: 12345678A)
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setTipoUsuario(null)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: 'transparent',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        color: '#6b7280',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      ← Volver
                    </button>
                    <button
                      type="submit"
                      style={{
                        flex: 1,
                        padding: '14px 24px',
                        background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      Continuar
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Pantalla de calendario
  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f9fafb',
      padding: '16px'
    }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Botón Nueva Cita flotante - SOLO para pacientes registrados */}
      {pacienteInfo && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 100
        }}>
          <button
            onClick={() => abrirModalNuevaCita()}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#3cbf9a',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 'bold'
            }}
          >
            +
          </button>
        </div>
      )}
      
      {/* Info del paciente */}
      {pacienteInfo && (
        <div style={{
          maxWidth: '100%',
          margin: '0 auto 16px',
          background: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3cbf9a 0%, #2ea885 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '18px'
            }}>
              {pacienteInfo.nombre.charAt(0)}{pacienteInfo.apellidos.charAt(0)}
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                {pacienteInfo.nombre} {pacienteInfo.apellidos}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                DNI: {pacienteInfo.dni}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {clienteInfo?.razon_social}
          </div>
        </div>
      )}
      
      {/* Controles de navegación del mes */}
      <div style={{ 
        maxWidth: '100%',
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => cambiarMes(-1)}
          style={{
            padding: '8px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          <ChevronLeft size={20} color="#4b5563" />
        </button>
        
        <div style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          minWidth: '220px',
          textAlign: 'center',
          textTransform: 'capitalize'
        }}>
          {mesActual.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
        
        <button
          onClick={() => cambiarMes(1)}
          style={{
            padding: '8px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          <ChevronRight size={20} color="#4b5563" />
        </button>
        
        <button
          onClick={irHoy}
          style={{
            padding: '8px 16px',
            border: 'none',
            borderRadius: '8px',
            background: '#3cbf9a',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Hoy
        </button>
      </div>

      {/* CALENDARIO */}
      <div style={{ 
        minHeight: '600px',
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '8px'
      }}>
        {/* Cabecera días de la semana */}
        {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map(dia => (
          <div 
            key={dia} 
            style={{
              padding: '16px',
              textAlign: 'center',
              fontWeight: '600',
              fontSize: '14px',
              color: '#374151',
              backgroundColor: '#f9fafb',
              borderRadius: '8px 8px 0 0',
              borderBottom: '2px solid #e5e7eb'
            }}
          >
            {dia}
          </div>
        ))}
        
        {/* Días del mes */}
        {obtenerDiasDelMes().map((dia, index) => {
          const citasDelDia = obtenerCitasDelDia(dia.fecha);
          const isToday = esHoy(dia.fecha);
          const mostrarBotonVerTodo = citasDelDia.length > 3;
          const citasVisibles = mostrarBotonVerTodo ? citasDelDia.slice(0, 3) : citasDelDia;
          
          return (
            <div
              key={index}
              onClick={() => dia.mesActual && pacienteInfo && abrirModalNuevaCita(dia.fecha)}
              style={{
                backgroundColor: dia.mesActual ? '#ffffff' : '#f3f4f6',
                border: isToday ? '2px solid #3cbf9a' : '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px',
                minHeight: '120px',
                cursor: (dia.mesActual && pacienteInfo) ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                opacity: dia.mesActual ? 1 : 0.6
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                fontSize: '16px',
                fontWeight: isToday ? '700' : '600',
                color: isToday ? '#ffffff' : (dia.mesActual ? '#1f2937' : '#9ca3af'),
                backgroundColor: isToday ? '#3cbf9a' : 'transparent',
                marginBottom: '8px'
              }}>
                {dia.fecha.getDate()}
              </div>
              
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {citasVisibles.map(cita => {
                  const color = getColorCita(cita);
                  const bgColor = lightenColor(color, 85);
                  const estadoRegistro = cita.estado_registro || 'aprobada';
                  
                  return (
                    <div
                      key={cita.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCitaSeleccionada(cita);
                        setShowDetalleModal(true);
                      }}
                      style={{
                        backgroundColor: bgColor,
                        borderLeft: `3px solid ${color}`,
                        borderRadius: '4px',
                        padding: '6px 8px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        minHeight: '48px',
                        position: 'relative'
                      }}
                    >
                      {estadoRegistro !== 'aprobada' && (
                        <div style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          fontSize: '10px',
                          background: estadoRegistro === 'pendiente_aprobacion' ? '#fbbf24' : '#ef4444',
                          color: 'white',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          fontWeight: 'bold'
                        }}>
                          {ESTADOS_REGISTRO[estadoRegistro].icon}
                        </div>
                      )}

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontWeight: '600',
                        color: color,
                        fontSize: '11px'
                      }}>
                        <Clock size={10} />
                        <span>{cita.hora_inicio}</span>
                      </div>
                      
                      <div style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '11px'
                      }}>
                        {cita.motivo || 'Cita'}
                      </div>
                      
                      {cita.estado !== 'programada' && (
                        <div style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '9px',
                          fontWeight: '600',
                          backgroundColor: ESTADOS_CITA[cita.estado]?.color || color,
                          color: '#ffffff',
                          marginTop: '2px'
                        }}>
                          {ESTADOS_CITA[cita.estado]?.label || cita.estado}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {mostrarBotonVerTodo && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirModalTodasCitas(dia.fecha, citasDelDia);
                    }}
                    style={{
                      padding: '6px 8px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '600',
                      color: '#3cbf9a',
                      backgroundColor: 'rgba(60, 191, 154, 0.1)',
                      border: '1px solid #3cbf9a',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      marginTop: '4px'
                    }}
                  >
                    <MoreHorizontal size={14} />
                    Ver todo ({citasDelDia.length})
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL: Nueva Cita */}
      {showCitaModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowCitaModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: '#3cbf9a',
                color: 'white',
                padding: '20px',
                borderRadius: '12px 12px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                Nueva Solicitud de Cita
              </h2>
              <button
                onClick={() => setShowCitaModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            <form id="form-nueva-cita" onSubmit={guardarCita} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Mostrar info si es paciente registrado */}
              {pacienteInfo && (
                <div style={{
                  padding: '12px',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#166534'
                }}>
                  <strong>✓ Paciente:</strong> {pacienteInfo.nombre} {pacienteInfo.apellidos}
                </div>
              )}

              {/* Datos del paciente si es NUEVO */}
              {!pacienteInfo && (
                <>
                  <div style={{
                    padding: '12px',
                    background: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#92400e'
                  }}>
                    <strong>Nuevo Paciente</strong>
                    <div style={{ marginTop: '4px', fontSize: '12px' }}>
                      ℹ️ Complete sus datos. La cita requerirá aprobación de la clínica.
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                      <CreditCard size={14} style={{ display: 'inline', marginRight: '6px' }} />
                      DNI *
                    </label>
                    <input
                      type="text"
                      value={citaForm.dni}
                      disabled
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: '#f3f4f6',
                        color: '#6b7280'
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Nombre *
                      </label>
                      <input
                        type="text"
                        value={citaForm.nombre}
                        onChange={(e) => setCitaForm({...citaForm, nombre: e.target.value})}
                        placeholder="Su nombre"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        Apellidos *
                      </label>
                      <input
                        type="text"
                        value={citaForm.apellidos}
                        onChange={(e) => setCitaForm({...citaForm, apellidos: e.target.value})}
                        placeholder="Sus apellidos"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        <Phone size={14} style={{ display: 'inline', marginRight: '6px' }} />
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        value={citaForm.telefono}
                        onChange={(e) => setCitaForm({...citaForm, telefono: e.target.value})}
                        placeholder="612345678"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                        <Mail size={14} style={{ display: 'inline', marginRight: '6px' }} />
                        Email *
                      </label>
                      <input
                        type="email"
                        value={citaForm.email}
                        onChange={(e) => setCitaForm({...citaForm, email: e.target.value})}
                        placeholder="email@ejemplo.com"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
              
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Fecha *
                </label>
                <input
                  type="date"
                  value={citaForm.fecha}
                  onChange={(e) => setCitaForm({...citaForm, fecha: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Hora Inicio *
                  </label>
                  <input
                    type="time"
                    value={citaForm.hora_inicio}
                    onChange={(e) => setCitaForm({...citaForm, hora_inicio: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Hora Fin *
                  </label>
                  <input
                    type="time"
                    value={citaForm.hora_fin}
                    onChange={(e) => setCitaForm({...citaForm, hora_fin: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Motivo
                </label>
                <input
                  type="text"
                  value={citaForm.motivo}
                  onChange={(e) => setCitaForm({...citaForm, motivo: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  placeholder="Motivo de la consulta"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Notas adicionales
                </label>
                <textarea
                  value={citaForm.notas}
                  onChange={(e) => setCitaForm({...citaForm, notas: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Información adicional"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowCitaModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#3cbf9a',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Solicitar Cita
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Conflicto de Horario */}
      {showConflictoModal && conflictoData && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}
          onClick={() => setShowConflictoModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: '#fbbf24',
              color: 'white',
              padding: '20px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} />
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                Horario No Disponible
              </h2>
            </div>

            <div style={{ padding: '24px' }}>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                El horario seleccionado no está disponible. Le sugerimos los siguientes horarios alternativos:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {conflictoData.horariosAlternativos.length > 0 ? (
                  conflictoData.horariosAlternativos.map((horario, index) => (
                    <button
                      key={index}
                      onClick={() => guardarCitaConHorarioAlternativo(horario)}
                      style={{
                        padding: '16px',
                        background: 'white',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3cbf9a';
                        e.currentTarget.style.background = '#f0fdf4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Clock size={16} color="#3cbf9a" />
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                          {horario.hora_inicio} - {horario.hora_fin}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280', marginLeft: '24px' }}>
                        Click para seleccionar este horario
                      </div>
                    </button>
                  ))
                ) : (
                  <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', padding: '16px' }}>
                    No hay horarios alternativos disponibles. Por favor, seleccione otra fecha.
                  </p>
                )}
              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowConflictoModal(false);
                    setShowCitaModal(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}
                >
                  Volver al Formulario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Todas las Citas del Día */}
      {showTodasCitasModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowTodasCitasModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: '#3cbf9a',
              color: 'white',
              padding: '20px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                  Citas del Día
                </h2>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                  {fechaDiaSeleccionado && formatearFechaLocal(fechaDiaSeleccionado.toISOString())}
                </p>
              </div>
              <button
                onClick={() => setShowTodasCitasModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {citasDelDiaSeleccionado.map(cita => {
                const color = getColorCita(cita);
                const bgColor = lightenColor(color, 90);
                const estadoRegistro = cita.estado_registro || 'aprobada';
                
                return (
                  <div
                    key={cita.id}
                    onClick={() => {
                      setShowTodasCitasModal(false);
                      setCitaSeleccionada(cita);
                      setShowDetalleModal(true);
                    }}
                    style={{
                      background: bgColor,
                      border: `2px solid ${color}`,
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={18} color={color} />
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                          {cita.hora_inicio} - {cita.hora_fin}
                        </span>
                      </div>
                      {estadoRegistro !== 'aprobada' && (
                        <div style={{
                          fontSize: '11px',
                          background: estadoRegistro === 'pendiente_aprobacion' ? '#fbbf24' : '#ef4444',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: 'bold'
                        }}>
                          {ESTADOS_REGISTRO[estadoRegistro].icon} {ESTADOS_REGISTRO[estadoRegistro].label}
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}>
                      {cita.motivo || 'Sin motivo especificado'}
                    </div>

                    {cita.notas && (
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                        {cita.notas}
                      </div>
                    )}

                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: color,
                        color: '#ffffff'
                      }}>
                        {ESTADOS_CITA[cita.estado]?.label || cita.estado}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Detalle de Cita */}
      {showDetalleModal && citaSeleccionada && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowDetalleModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: getColorCita(citaSeleccionada),
              color: 'white',
              padding: '20px',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700' }}>
                Detalle de la Cita
              </h2>
              <button
                onClick={() => setShowDetalleModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {/* Estado de Registro */}
              {citaSeleccionada.estado_registro && citaSeleccionada.estado_registro !== 'aprobada' && (
                <div style={{
                  padding: '12px',
                  background: citaSeleccionada.estado_registro === 'pendiente_aprobacion' ? '#fef3c7' : '#fee2e2',
                  border: `1px solid ${citaSeleccionada.estado_registro === 'pendiente_aprobacion' ? '#fbbf24' : '#ef4444'}`,
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: citaSeleccionada.estado_registro === 'pendiente_aprobacion' ? '#92400e' : '#991b1b'
                }}>
                  <strong>{ESTADOS_REGISTRO[citaSeleccionada.estado_registro].icon} {ESTADOS_REGISTRO[citaSeleccionada.estado_registro].label}</strong>
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>
                    {citaSeleccionada.estado_registro === 'pendiente_aprobacion' && 'Su cita está pendiente de aprobación por parte de la clínica.'}
                    {citaSeleccionada.estado_registro === 'rechazada' && 'Su solicitud de cita ha sido rechazada.'}
                  </div>
                </div>
              )}

              {/* Fecha y Hora */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Calendar size={18} color="#6b7280" />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Fecha y Hora</span>
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                  {formatearFechaLocal(citaSeleccionada.fecha)}
                </div>
                <div style={{ fontSize: '15px', color: '#1f2937', marginTop: '4px' }}>
                  {citaSeleccionada.hora_inicio} - {citaSeleccionada.hora_fin}
                </div>
              </div>

              {/* Motivo */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Motivo
                </div>
                <div style={{ fontSize: '15px', color: '#1f2937' }}>
                  {citaSeleccionada.motivo || 'Sin motivo especificado'}
                </div>
              </div>

              {/* Notas */}
              {citaSeleccionada.notas && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                    Notas
                  </div>
                  <div style={{ fontSize: '14px', color: '#1f2937' }}>
                    {citaSeleccionada.notas}
                  </div>
                </div>
              )}

              {/* Médico */}
              {citaSeleccionada.medico_nombre && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Stethoscope size={18} color="#6b7280" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Médico</span>
                  </div>
                  <div style={{ fontSize: '15px', color: '#1f2937' }}>
                    {citaSeleccionada.medico_nombre}
                  </div>
                  {citaSeleccionada.medico_especialidad && (
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                      {citaSeleccionada.medico_especialidad}
                    </div>
                  )}
                </div>
              )}

              {/* Consulta */}
              {citaSeleccionada.consulta_nombre && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <MapPin size={18} color="#6b7280" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280' }}>Consulta</span>
                  </div>
                  <div style={{ fontSize: '15px', color: '#1f2937' }}>
                    {citaSeleccionada.consulta_nombre}
                  </div>
                </div>
              )}

              {/* Estado */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                  Estado
                </div>
                <span style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  borderRadius: '16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: ESTADOS_CITA[citaSeleccionada.estado]?.color || '#3498db',
                  color: '#ffffff'
                }}>
                  {ESTADOS_CITA[citaSeleccionada.estado]?.label || citaSeleccionada.estado}
                </span>
              </div>

              {/* Botón Cerrar */}
              <button
                onClick={() => setShowDetalleModal(false)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#f3f4f6',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '8px'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}